import React, { useState, useEffect } from 'react'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Box,
} from '@material-ui/core'
import {
  ArrowBack,
  ArrowForward,
  GetApp,
  Publish,
  Visibility,
  ExitToApp,
} from '@material-ui/icons'
import { Wand } from '../../components/Icons'
import { callLlmGateway } from '../../services/LlmGatewayService'
import {
  IJsonFlowEditorData,
  IPromptVersion,
} from '../../models/JsonFlowEditor'
import { DataFlow } from '../../models/DataFlow'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(3),
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    textField: {
      marginBottom: theme.spacing(1),
    },
    versionInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(1),
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: theme.spacing(2),
    },
    actionButtons: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    navigationButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    jsonPreview: {
      padding: theme.spacing(2),
      backgroundColor: '#f5f5f5',
      borderRadius: theme.shape.borderRadius,
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      overflowY: 'auto',
      maxHeight: '400px',
    },
  }),
)

const StandaloneJsonEditor: React.FC = () => {
  const classes = useStyles()
  const [showJsonPreview, setShowJsonPreview] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [editorData, setEditorData] = useState<IJsonFlowEditorData>({
    userPrompt: {
      current: '',
      versions: [],
      currentVersionIndex: -1,
    },
    systemPrompt: {
      current: '',
      versions: [],
      currentVersionIndex: -1,
    },
    dataflow: null,
  })

  useEffect(() => {
    const savedData = localStorage.getItem('jsonFlowEditorData')
    if (savedData) {
      try {
        setEditorData(JSON.parse(savedData))
      } catch (error) {
        console.error('Error parsing saved data:', error)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('jsonFlowEditorData', JSON.stringify(editorData))
  }, [editorData])

  const handleUserPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditorData((prev) => ({
      ...prev,
      userPrompt: {
        ...prev.userPrompt,
        current: e.target.value,
      },
    }))
  }

  const handleSystemPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditorData((prev) => ({
      ...prev,
      systemPrompt: {
        ...prev.systemPrompt,
        current: e.target.value,
      },
    }))
  }

  const saveVersion = (type: 'userPrompt' | 'systemPrompt') => {
    const currentContent = editorData[type].current
    if (!currentContent.trim()) return

    const newVersion: IPromptVersion = {
      content: currentContent,
      timestamp: new Date().toISOString(),
      user: 'User', // Replace with actual user name if available
      version: editorData[type].versions.length + 1,
    }

    setEditorData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        versions: [...prev[type].versions, newVersion],
        currentVersionIndex: prev[type].versions.length,
      },
    }))
  }

  const navigateVersion = (
    type: 'userPrompt' | 'systemPrompt',
    direction: 'prev' | 'next',
  ) => {
    const { versions, currentVersionIndex } = editorData[type]
    if (versions.length === 0) return

    let newIndex = currentVersionIndex
    if (direction === 'prev' && currentVersionIndex > 0) {
      newIndex = currentVersionIndex - 1
    } else if (
      direction === 'next' &&
      currentVersionIndex < versions.length - 1
    ) {
      newIndex = currentVersionIndex + 1
    }

    setEditorData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        current: versions[newIndex].content,
        currentVersionIndex: newIndex,
      },
    }))
  }

  const handleExecute = async () => {
    if (!editorData.userPrompt.current || !editorData.systemPrompt.current) {
      alert(
        'Por favor, preencha os campos de prompt do usuário e prompt do sistema.',
      )
      return
    }

    setIsProcessing(true)
    try {
      const result = await callLlmGateway(
        editorData.systemPrompt.current,
        editorData.userPrompt.current,
      )

      try {
        const parsedResult = JSON.parse(result) as DataFlow
        setEditorData((prev) => ({
          ...prev,
          dataflow: parsedResult,
        }))
        setShowJsonPreview(true)
      } catch (error) {
        console.error('Error parsing LLM response as JSON:', error)
        alert('A resposta do LLM não é um JSON válido.')
      }
    } catch (error) {
      console.error('Error calling LLM Gateway:', error)
      alert(
        'Erro ao chamar o LLM Gateway. Verifique o console para mais detalhes.',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleImportJson = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (event) => {
          try {
            const jsonData = JSON.parse(
              event.target?.result as string,
            ) as DataFlow
            setEditorData((prev) => ({
              ...prev,
              dataflow: jsonData,
            }))
            setShowJsonPreview(true)
          } catch (error) {
            console.error('Error parsing imported JSON:', error)
            alert('O arquivo importado não é um JSON válido.')
          }
        }
        reader.readAsText(file)
      }
    }
    input.click()
  }

  const handleExportJson = () => {
    if (!editorData.dataflow) {
      alert('Não há dados para exportar.')
      return
    }

    const jsonString = JSON.stringify(editorData.dataflow, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `dataflow_${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExit = () => {
    localStorage.setItem('jsonFlowEditorData', JSON.stringify(editorData))

    window.location.href = '/'
  }

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString()
  }

  return (
    <Container className={classes.root} maxWidth="lg">
      <div className={classes.header}>
        <Typography variant="h4">
          Editor de JSON de Fluxo Conversacional
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ExitToApp />}
          onClick={handleExit}
        >
          Sair
        </Button>
      </div>

      <Paper className={classes.paper}>
        <Typography variant="h6">Prompt do Usuário</Typography>
        <TextField
          className={classes.textField}
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          value={editorData.userPrompt.current}
          onChange={handleUserPromptChange}
          placeholder="Digite o prompt do usuário aqui..."
          InputProps={{
            endAdornment: (
              <Tooltip title="Melhorar script">
                <IconButton>
                  <Wand />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
        <div className={classes.versionInfo}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveVersion('userPrompt')}
          >
            Salvar
          </Button>
          {editorData.userPrompt.currentVersionIndex >= 0 && (
            <Typography variant="body2">
              Versão{' '}
              {
                editorData.userPrompt.versions[
                  editorData.userPrompt.currentVersionIndex
                ].version
              }{' '}
              -
              {formatDate(
                editorData.userPrompt.versions[
                  editorData.userPrompt.currentVersionIndex
                ].timestamp,
              )}{' '}
              -
              {
                editorData.userPrompt.versions[
                  editorData.userPrompt.currentVersionIndex
                ].user
              }
            </Typography>
          )}
          <div className={classes.navigationButtons}>
            <IconButton
              disabled={editorData.userPrompt.currentVersionIndex <= 0}
              onClick={() => navigateVersion('userPrompt', 'prev')}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              disabled={
                editorData.userPrompt.currentVersionIndex >=
                editorData.userPrompt.versions.length - 1
              }
              onClick={() => navigateVersion('userPrompt', 'next')}
            >
              <ArrowForward />
            </IconButton>
          </div>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography variant="h6">System Prompt</Typography>
        <TextField
          className={classes.textField}
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          value={editorData.systemPrompt.current}
          onChange={handleSystemPromptChange}
          placeholder="Digite o system prompt aqui..."
        />
        <div className={classes.versionInfo}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveVersion('systemPrompt')}
          >
            Salvar
          </Button>
          {editorData.systemPrompt.currentVersionIndex >= 0 && (
            <Typography variant="body2">
              Versão{' '}
              {
                editorData.systemPrompt.versions[
                  editorData.systemPrompt.currentVersionIndex
                ].version
              }{' '}
              -
              {formatDate(
                editorData.systemPrompt.versions[
                  editorData.systemPrompt.currentVersionIndex
                ].timestamp,
              )}{' '}
              -
              {
                editorData.systemPrompt.versions[
                  editorData.systemPrompt.currentVersionIndex
                ].user
              }
            </Typography>
          )}
          <div className={classes.navigationButtons}>
            <IconButton
              disabled={editorData.systemPrompt.currentVersionIndex <= 0}
              onClick={() => navigateVersion('systemPrompt', 'prev')}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              disabled={
                editorData.systemPrompt.currentVersionIndex >=
                editorData.systemPrompt.versions.length - 1
              }
              onClick={() => navigateVersion('systemPrompt', 'next')}
            >
              <ArrowForward />
            </IconButton>
          </div>
        </div>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.buttonGroup}>
            <div className={classes.actionButtons}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExecute}
                disabled={isProcessing}
                startIcon={isProcessing ? null : <Wand />}
              >
                {isProcessing ? 'Processando...' : 'Executar'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowJsonPreview(!showJsonPreview)}
                startIcon={<Visibility />}
              >
                {showJsonPreview ? 'Ocultar JSON' : 'Visualizar JSON'}
              </Button>
            </div>
            <div className={classes.actionButtons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleImportJson}
                startIcon={<Publish />}
              >
                Importar JSON
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportJson}
                startIcon={<GetApp />}
                disabled={!editorData.dataflow}
              >
                Exportar JSON
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>

      {showJsonPreview && editorData.dataflow && (
        <Paper className={classes.paper}>
          <Typography variant="h6">Visualização do JSON</Typography>
          <Box className={classes.jsonPreview}>
            {JSON.stringify(editorData.dataflow, null, 2)}
          </Box>
        </Paper>
      )}
    </Container>
  )
}

export default StandaloneJsonEditor
